import { useRef } from 'react'

import { sendToApi } from 'api/client'
import { validationSchema } from 'components/Wizards/BrandStyleExtractionWizard/UrlInput'
import { AiClient, GptPayload } from 'api/clients/AiClient'
import { WebsiteScrapingError } from 'api/clients/BrandVoiceDetectionClient'

const gpt4Payload = (sampleText: string): GptPayload => {
  return {
    model: 'gpt-4o',
    temperature: 0.7,
    messages: [
      {
        role: 'system',
        content:
          `Your task is to read the result of crawling a web page and determine whether the response is website content or if the crawler ran into an issue. ` +
          `The document comes from a crawled web page/content which the user has access to\n` +
          `You will receive a document that is the text content pulled from a web page` +
          ` and must evaluate whether the scraper succesfully fetched the content or not\n` +
          `For example, if the scraper fetched a login page, a blocked message, a robot check, or an empty page, you should notify the user that the scraper couldn't fetch the page they intended\n` +
          `If the scraped content includes what seems to be the content of a cookie banner, you should remove it from the content and return the content without the cookie banner\n` +
          `If the scraper fetched the content the user intended, you should return the content to the user.` +
          `Your response should only include the content, do not include any additional information or instructions in your response.`,
      },
      {
        role: 'user',
        content: sampleText,
      },
    ],
    functions: [
      {
        name: 'notifyError',
        description:
          'Call this function when you suspect that the crawler couldn\'t fetch the page the user intended. This happens when, instead of the page the user wanted to import, our crawler instead received some other page that is probably not the one the user had in mind. For example, the user will try to import a page containing a blog post or a short story, but our crawler instead receives a page containing a login form, a "verify you are not a robot" page, an error message, etc.',
        parameters: {
          type: 'object',
          properties: {
            errorCode: {
              type: 'string',
              description: 'The category of error that occurred, if that can be determined.\n',
              enum: ['serverError', 'loginPage', 'blockedMessage', 'robotCheck', 'emptyPage', 'paymentWall', 'unknown'],
            },
            assessment: {
              type: 'string',
              description:
                '(optional) Do you have a message for the user or admin? This is probably not needed if you are using an `errorCode`.\n',
            },
          },
          required: ['errorCode'],
        },
      },
    ],
  }
}

export enum WebsiteScrapingErrorCode {
  BlockedMessage = 'blockedMessage',
  CookieBanner = 'cookieBanner',
  LoginPage = 'loginPage',
  PaymentWall = 'paymentWall',
  RobotCheck = 'robotCheck',
  ServerError = 'serverError',
  Unknown = 'unknown',
}

const scanText = async (text: string) => {
  // a token is, using a very crude approximation, equivalent to 4 English characters
  // and GPT4 has a max context length of 8k tokens so we limit the
  // sample to 5k "tokens", leaving 2k for the prompt and 1k of leeway
  const sampleText = text.slice(0, 5 * 1024 * 4)
  const gpt4response = await AiClient.chat(gpt4Payload(sampleText), undefined, {
    fallbackPayloads: [],
  })

  if (gpt4response.choices[0].finish_reason === 'function_call') {
    try {
      const errorParams: { errorCode: string; assessment?: string } = JSON.parse(
        gpt4response.choices[0].message.function_call.arguments,
      )
      throw new WebsiteScrapingError({ cause: { text }, errorCode: errorParams.errorCode as WebsiteScrapingErrorCode })
    } catch (error) {
      if (error instanceof WebsiteScrapingError) {
        throw error
      }
      throw new WebsiteScrapingError({ cause: { text }, errorCode: WebsiteScrapingErrorCode.Unknown })
    }
  }

  // TODO: Migrate the errors in brand voice detection to use tool calls
  if (gpt4response.choices[0].finish_reason === 'tool_calls') {
    throw new WebsiteScrapingError({ cause: { text }, errorCode: WebsiteScrapingErrorCode.Unknown })
  }

  return gpt4response.choices[0].message.content
}

export const useFetchReference = () => {
  const abortController = useRef<AbortController | null>(null)
  if (abortController.current === null) {
    abortController.current = new AbortController()
  }

  const handleCancel = () => {
    abortController?.current?.abort()
  }

  const fetchReference = async (value: string, onAbortScanWebsite: () => void) => {
    abortController.current = new AbortController()
    const url = validationSchema.cast(value)

    const browserlessResponse = await sendToApi(`/scrape_url`, {
      method: 'POST',
      signal: abortController.current?.signal,
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ url }),
    })

    if (browserlessResponse.type === 'exception' && browserlessResponse.data.name === 'AbortError') {
      onAbortScanWebsite()
      return
    }
    const content = await scanText(browserlessResponse.data)

    return content
  }

  return { fetchReference, handleCancel }
}
